import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import useTranslate from '../../../../../../lib/hooks/use-translate'
import { asthmaSeverityMapping } from '../../../../../../lib/types/enums/affected-staff-questionnaire/asthma-severity.enum'
import { interestEnumMapping } from '../../../../../../lib/types/enums/affected-staff-questionnaire/intrest.enum'
import { personalityEnumMapping } from '../../../../../../lib/types/enums/affected-staff-questionnaire/personality.enum'
import { AffectedStaffQuestionnaire } from '../../../../../../lib/types/models/affected-staff-questionnaire/affected-staff-questionnaire'
import ImageViewCard from '../../../../../elements/image-view-card'
import LabelWithText from '../../../../ols/label-with-text'

type Props = {
  affectedStaffName: string
  questionnaire: AffectedStaffQuestionnaire
}

const StaffPersonalSpaceQuestionnaireView = ({ questionnaire, affectedStaffName }: Props) => {
  const { t } = useTranslate()

  const formatBoolean = (value?: boolean | null) => {
    if (value === true) return t('staff.personal.space.yes')
    if (value === false) return t('staff.personal.space.no')
    return '-'
  }

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
        <div className="flex flex-col gap-3">
          <InnerBox
            title={t('affected.staff.questionnaire.personality.title')}
            className="p-4 rounded-lg"
          >
            <div className="flex flex-col gap-3">
              <LabelWithText
                label={t('affected.staff.questionnaire.personality.closer.to')}
                data={
                  questionnaire.personalityTraits && questionnaire.personalityTraits.length > 0
                    ? questionnaire.personalityTraits
                        .map((trait) => t(personalityEnumMapping[trait]))
                        .join(', ')
                    : '-'
                }
              />
              <LabelWithText
                label={t(
                  'affected.staff.questionnaire.personality.have.religious.affiliation.question'
                )}
                data={
                  <div className="flex items-center gap-1">
                    <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                      {formatBoolean(questionnaire.hasReligiousAffiliation)}
                    </Typography>
                    {questionnaire.otherReligiousAffiliation && (
                      <Badge
                        size="flexible"
                        text={questionnaire.otherReligiousAffiliation}
                        variant="primary"
                      />
                    )}
                  </div>
                }
              />
              <LabelWithText
                label={t('affected.staff.questionnaire.personality.interest')}
                data={
                  questionnaire.interests && questionnaire.interests.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {questionnaire.interests.map((interest) => (
                        <Badge
                          key={interest}
                          size="flexible"
                          text={t(interestEnumMapping[interest])}
                        />
                      ))}
                    </div>
                  ) : (
                    '-'
                  )
                }
              />
              <LabelWithText
                label={t(
                  'affected.staff.questionnaire.personality.accept.living.with.family.of.different.ethnic.background.question'
                )}
                data={formatBoolean(questionnaire.willingToStayWithDifferentEthnicBackground)}
              />
            </div>
          </InnerBox>
          {questionnaire.hobbies && questionnaire.hobbies.length > 0 && (
            <InnerBox title={t('hobbies.title')} className="p-4 rounded-lg">
              <div className="flex items-center gap-1">
                <div className="flex flex-wrap gap-1">
                  {questionnaire.hobbies.map((hobby, index) => (
                    <Badge key={index} size="small" text={hobby.name} variant="primary" />
                  ))}
                </div>
              </div>
            </InnerBox>
          )}
        </div>
        <div className="flex flex-col gap-3">
          <InnerBox
            title={t('affected.staff.questionnaire.health.information.title')}
            className="p-4 rounded-lg"
          >
            <div className="flex flex-col gap-2">
              <LabelWithText
                label={t('affected.staff.questionnaire.health.information.are.you.in.good.health')}
                data={formatBoolean(questionnaire.isInGoodHealth)}
              />
              <LabelWithText
                label={t('affected.staff.questionnaire.health.information.do.you.have.asthma')}
                data={
                  <div className="flex items-center gap-1">
                    <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                      {formatBoolean(questionnaire.hasAsthma)}
                    </Typography>
                    {questionnaire.asthmaSeverity && (
                      <Badge
                        size="flexible"
                        text={t(asthmaSeverityMapping[questionnaire.asthmaSeverity])}
                        variant="primary"
                      />
                    )}
                  </div>
                }
              />
              <LabelWithText
                label={t('affected.staff.questionnaire.health.information.do.you.have.allergy')}
                data={
                  <div className="flex items-center gap-1">
                    <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                      {formatBoolean(questionnaire.hasAllergies)}
                    </Typography>
                    {questionnaire.allergyType && (
                      <Badge size="flexible" text={questionnaire.allergyType} variant="primary" />
                    )}
                  </div>
                }
              />
              <LabelWithText
                label={t('affected.staff.questionnaire.health.information.do.you.smoke')}
                data={formatBoolean(questionnaire.isSmoker)}
              />
              <LabelWithText
                label={t('affected.staff.questionnaire.health.information.other.information')}
                data={valueRender(questionnaire.otherInformationInEnglish)}
              />
              <Box className="bg-primary-light rounded p-3">
                <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                  {t('affected.staff.questionnaire.health.information.health.note')}
                </Typography>
              </Box>
            </div>
          </InnerBox>
          <InnerBox
            title={t('affected.staff.questionnaire.driver.license.title')}
            className="p-4 rounded-lg"
          >
            <LabelWithText
              label={t('affected.staff.questionnaire.driver.license.do.you.have.driver.license')}
              data={
                <div className="flex items-center gap-1">
                  <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
                    {formatBoolean(questionnaire.hasDriversLicense)}
                  </Typography>

                  {questionnaire.drivesRegularly && (
                    <Badge
                      size="flexible"
                      text={t('affected.staff.questionnaire.drives.regularly')}
                      variant="primary"
                    />
                  )}
                </div>
              }
            />
            <div className="h-2" />
            <Box className="bg-primary-light rounded p-3">
              <Typography variant="label-semibold" color={tailwindColors.primary.DEFAULT}>
                {t('affected.staff.questionnaire.driver.license.note')}
              </Typography>
            </Box>
          </InnerBox>
        </div>
      </div>
      {questionnaire.hostFamilyLetter && (
        <InnerBox
          title={t('affected.staff.questionnaire.host.family.letter')}
          className="p-4 rounded-lg mt-3.5"
        >
          <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
            {t('affected.staff.questionnaire.host.family.letter.note')}
          </Typography>
          <div className="h-2" />
          <InnerBox className="bg-white-hover p-3 ">
            <Typography variant="label-semibold" color={tailwindColors.grey.DEFAULT}>
              {questionnaire.hostFamilyLetter}
            </Typography>
          </InnerBox>
        </InnerBox>
      )}
      {questionnaire.familyImages?.length > 0 && (
        <InnerBox className="rounded-lg p-4 mt-3.5  flex flex-col gap-3">
          <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
            {t('affected.staff.questionnaire.host.family.pictures.title')}
          </Typography>
          <div className="grid grid-cols-2 gap-3 mobile:grid-cols-1">
            {questionnaire.familyImages.map((picture) => (
              <ImageViewCard key={picture.id} picture={picture} />
            ))}
          </div>
        </InnerBox>
      )}
      <div className="h-3.5" />
      <InnerBox
        title={t('affected.staff.questionnaire.medical.release')}
        className="p-4 rounded-lg mobile:mb-12"
      >
        <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
          {`${t('affected.staff.questionnaire.medical.release.note.1')} ${affectedStaffName}, ${t('affected.staff.questionnaire.medical.release.note.2')}`}
        </Typography>
      </InnerBox>
    </div>
  )
}

export default StaffPersonalSpaceQuestionnaireView
